<script lang="ts">
  import { DECK, INTERCOM_COPY, hops, receivedQuality, type NodeId } from "@spacecrew/shared";
  import { ship } from "../net/ship.svelte.js";
  import { session } from "../net/session.svelte.js";
  import { intercom } from "./store.svelte.js";
  import { FeedRenderer, portraitCanvas } from "./shader.js";
  import { radio } from "./radio.js";

  let { sessionId, self = false }: { sessionId: string; self?: boolean } = $props();

  const crew = $derived(ship.snap?.crew[sessionId]);
  const me = $derived(ship.snap?.crew[session.sessionId]);
  const peer = $derived(intercom.peers[sessionId]);
  const stream = $derived(self ? intercom.localStream : (peer?.stream ?? null));
  const hasVideo = $derived(self ? intercom.mode === "av" : (!!peer?.hasVideo && crew?.intercom === "av"));
  const where = $derived(crew ? (crew.toNodeId || crew.nodeId) as NodeId : "bridge");
  const distance = $derived(me && crew ? hops((me.toNodeId || me.nodeId) as NodeId, where) : 0);
  const quality = $derived.by(() => {
    if (self) return 1;
    if (!crew) return 0;
    if (intercom.debugQuality !== null) return intercom.debugQuality;
    const linked = peer?.state === "connected" || crew.intercom === "none" || crew.intercom === "";
    return linked ? receivedQuality(crew.commsQuality, distance) : 0;
  });
  const tint = $derived(where === "bridge" ? 0.0 : 1.0); // bridge = blue-white security cam, elsewhere = green phosphor

  let canvas: HTMLCanvasElement;
  let video: HTMLVideoElement | undefined = $state();
  let renderer: FeedRenderer | null = null;
  let portrait: HTMLCanvasElement | null = null;

  $effect(() => {
    renderer = new FeedRenderer(canvas);
    return () => { renderer?.destroy(); renderer = null; };
  });
  $effect(() => {
    if (!renderer) return;
    if (hasVideo && stream && video) {
      if (video.srcObject !== stream) { video.srcObject = stream; void video.play().catch(() => {}); }
      renderer.source = { element: video, quality, tint };
    } else {
      portrait ??= portraitCanvas(crew?.name ?? "?", sessionId);
      renderer.source = { element: portrait, quality, tint };
    }
  });
  $effect(() => { if (!self) radio.setQuality(sessionId, quality); });
  const bars = $derived(Math.round(quality * 5));
</script>

<figure class="tile" class:speaking={peer?.speaking} class:self>
  <canvas bind:this={canvas} width="160" height="120" aria-hidden="true"></canvas>
  <video bind:this={video} playsinline muted autoplay hidden></video>
  {#if !self && quality < 0.12}<span class="slate">{INTERCOM_COPY.noSignal}</span>{/if}
  {#if self && intercom.muted}<span class="slate">MIC MUTED</span>{/if}
  <figcaption>
    <span class="name">{crew?.name ?? "—"}{self ? " (YOU)" : ""}</span>
    <span class="where dim">{crew ? DECK.nodes[where].label : ""}</span>
    <span class="meter" aria-label="signal {Math.round(quality * 100)} %">{"▮".repeat(bars)}{"▯".repeat(5 - bars)}</span>
  </figcaption>
</figure>

<style>
  .tile { position: relative; margin: 0; width: 160px; border: 1px solid var(--fg-dim); background: #000; }
  .tile.speaking { border-color: var(--fg); box-shadow: 0 0 6px var(--fg-dim); }
  .tile.self { opacity: 0.85; }
  canvas { display: block; width: 160px; height: 120px; image-rendering: pixelated; }
  figcaption { display: grid; grid-template-columns: 1fr auto; gap: 0 0.4em; font-size: 0.75em; padding: 0.2em 0.4em; line-height: 1.3; }
  .where { grid-column: 1 / -1; }
  .meter { letter-spacing: -0.05em; }
  .slate { position: absolute; top: 50px; left: 0; right: 0; text-align: center; font-size: 0.8em; color: var(--warn); background: rgba(0,0,0,0.6); padding: 0.2em; }
  @media (prefers-reduced-motion: reduce) { .tile.speaking { box-shadow: none; } }
</style>
